import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart, faTruck, faCreditCard, faCheckCircle,
  faSpinner, faArrowLeft, faMapMarkerAlt, faPhone, faUser,
  faEnvelope, faWallet, faBuilding, faLock, faHome, faBriefcase,
  faEllipsisH, faStar, faPlus, faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { cartAPI, ordersAPI, paymentAPI, walletAPI, userAPI, paymentMethodsAPI, shippingAPI, recommendationsAPI, promotionsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { getCheckoutFeeRates, calculateLogisticsVat, calculateEscrowFee, DEFAULT_VAT_PERCENTAGE, DEFAULT_ESCROW_FEE_PERCENTAGE, APPLIED_PROMO_STORAGE_KEY } from '../utils/vat';
import { useNotifications } from '../context/NotificationContext';

// ── Shared Tailwind primitives for this page ────────────────────────────────
// Card surface used throughout: form sections, order summary, recommendations.
// Shadow is tinted toward the brand green rather than flat black, so cards
// feel like they belong to this page's palette instead of a generic default.
const CARD = 'tw-rounded-2xl tw-bg-white/95 dark:tw-bg-zinc-900/70 tw-backdrop-blur-xl tw-border tw-border-green-600/10 dark:tw-border-green-500/15 tw-shadow-[0_10px_40px_-12px_rgba(22,101,52,0.12)] dark:tw-shadow-[0_10px_40px_-8px_rgba(0,0,0,0.5)]';

const INPUT_BASE = 'tw-w-full tw-rounded-xl tw-border tw-bg-white dark:tw-bg-zinc-950/40 tw-px-4 tw-py-3 tw-text-[0.925rem] tw-text-zinc-900 dark:tw-text-zinc-100 placeholder:tw-text-zinc-400 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-green-500/25';

// Primary CTA: the site's own signature gradient (#22C55E → #16A34A, used in
// every header banner across the app), a green-tinted glow instead of a flat
// black shadow, and a lift-on-hover / press-on-click physical feel.
const BTN_PRIMARY = 'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-7 tw-py-3.5 tw-font-bold tw-text-[0.925rem] tw-tracking-tight tw-text-white tw-bg-[linear-gradient(135deg,#22C55E_0%,#16A34A_100%)] tw-shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55)] tw-transition-all tw-duration-200 hover:tw-shadow-[0_14px_36px_-6px_rgba(34,197,94,0.65)] hover:tw--translate-y-0.5 active:tw-translate-y-0 active:tw-scale-[0.97] disabled:tw-opacity-60 disabled:hover:tw-translate-y-0 disabled:hover:tw-shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55)]';

const BTN_SECONDARY = 'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-6 tw-py-3.5 tw-font-semibold tw-text-[0.925rem] tw-text-zinc-600 dark:tw-text-zinc-300 tw-border tw-border-zinc-200 dark:tw-border-zinc-700 tw-bg-white/60 dark:tw-bg-zinc-900/40 tw-transition-all tw-duration-200 hover:tw-border-zinc-300 dark:hover:tw-border-zinc-600 hover:tw-bg-zinc-50 dark:hover:tw-bg-zinc-800 active:tw-scale-[0.97] disabled:tw-opacity-50';

const Field = ({ label, icon, error, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1.5">
    <label className="tw-text-[0.8rem] tw-font-semibold tw-text-zinc-600 dark:tw-text-zinc-300 tw-flex tw-items-center tw-gap-1.5">
      {icon && <FontAwesomeIcon icon={icon} className="tw-text-green-500 tw-text-xs" />}
      {label}
    </label>
    {children}
    {error && <span className="tw-text-xs tw-font-medium tw-text-red-500">{error}</span>}
  </div>
);

const TextInput = (props) => {
  const { error, className = '', ...rest } = props;
  return (
    <input
      {...rest}
      className={`${INPUT_BASE} ${error ? 'tw-border-red-400 focus:tw-ring-red-500/25' : 'tw-border-zinc-200 dark:tw-border-zinc-700 focus:tw-border-green-500'} ${className}`}
    />
  );
};

// Custom radio indicator — native browser radio buttons read as a default/
// cheap control on a premium page, so this replaces them visually while an
// sr-only native input keeps real radio semantics for forms/accessibility.
const RadioDot = ({ checked }) => (
  <span
    className={`tw-relative tw-w-5 tw-h-5 tw-rounded-full tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-border-2 tw-transition-all tw-duration-200 ${
      checked ? 'tw-border-green-500' : 'tw-border-zinc-300 dark:tw-border-zinc-600'
    }`}
  >
    <span
      className={`tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-[linear-gradient(135deg,#22C55E_0%,#16A34A_100%)] tw-transition-all tw-duration-200 ${
        checked ? 'tw-scale-100 tw-opacity-100' : 'tw-scale-0 tw-opacity-0'
      }`}
    />
  </span>
);

const SectionCard = ({ icon, title, children }) => (
  <div className={`${CARD} tw-p-6 md:tw-p-8`}>
    <h2 className="tw-flex tw-items-center tw-gap-3 tw-text-lg tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50 tw-mb-6">
      <span className="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-green-500/10 tw-text-green-500 tw-flex tw-items-center tw-justify-center tw-text-sm">
        <FontAwesomeIcon icon={icon} />
      </span>
      {title}
    </h2>
    {children}
  </div>
);

const Checkout = () => {
  const { showCustomToast } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart, refreshCart } = useCart();

  // State management
  const [currentStep, setCurrentStep] = useState(1); // 1: Shipping, 2: Payment, 3: Review
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isBuyNow, setIsBuyNow] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(DEFAULT_VAT_PERCENTAGE);
  const [escrowFeePercentage, setEscrowFeePercentage] = useState(DEFAULT_ESCROW_FEE_PERCENTAGE);
  const [appliedPromo, setAppliedPromo] = useState(null);

  // VAT rate (for Logistics VAT) and Escrow Fee rate, shared with Cart.jsx —
  // see src/utils/vat.js.
  useEffect(() => {
    getCheckoutFeeRates().then(({ vatPercentage, escrowFeePercentage }) => {
      setVatPercentage(vatPercentage);
      setEscrowFeePercentage(escrowFeePercentage);
    });
  }, []);

  // Shipping information
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Nigeria',
    landmark: '',
    saveAddress: true
  });

  // Saved addresses state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'wallet', 'transfer', 'saved'
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [useEscrow, setUseEscrow] = useState(true);

  // Errors
  const [errors, setErrors] = useState({});

  // Checkout recommendations (Accessories)
  const [checkoutRecommendations, setCheckoutRecommendations] = useState([]);

  // Shipping fee from API (fallback ₦5,000 per doc)
  const [shippingFee, setShippingFee] = useState(5000);
  const [shippingEstimated, setShippingEstimated] = useState(true);
  const [calculatingShipping, setCalculatingShipping] = useState(false);

  // Fetch cart items and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Captured alongside setCartItems below so the mount-time shipping
        // prefetch (after the address block further down) can use the real
        // items without reading the cartItems state — which wouldn't have
        // re-rendered yet within this same async function.
        let itemsForShippingPrefetch = [];

        // Check if Buy Now flow (direct checkout from Shop)
        if (location.state?.buyNow && location.state?.items) {
          setIsBuyNow(true);
          setCartItems(location.state.items);
          itemsForShippingPrefetch = location.state.items;
        } else {
          // Normal checkout flow - Fetch cart
          const cartResponse = await cartAPI.getCart();
          if (cartResponse && cartResponse.data) {
            const items = cartResponse.data.items || cartResponse.data.cart?.items || [];
            const transformedItems = items.map(item => {
              // Extract image from product images array (same pattern as Cart/Shop/Homepage)
              let productImage = 'https://via.placeholder.com/200?text=No+Image';

              if (item.product?.images && item.product.images.length > 0) {
                const firstImage = item.product.images[0];
                productImage = typeof firstImage === 'string' ? firstImage : firstImage?.url;
              } else if (item.product?.image) {
                productImage = item.product.image;
              } else if (item.image) {
                productImage = item.image;
              }

              return {
                id: item._id || item.id,
                product_id: item.product_id || item.product?._id,
                name: item.product?.title || item.product?.name || item.name || 'Product',
                // The cart line's own price wins: the server resolves it per
                // buyer (sellers get the product's wholesale price), so the
                // product's retail price would misquote sellers here — and
                // disagree with the amount the order is actually created at.
                price: item.price ?? item.product?.price ?? 0,
                quantity: item.quantity || 1,
                image: productImage,
                seller_id: item.product?.seller_id || item.seller_id
              };
            });
            setCartItems(transformedItems);
            itemsForShippingPrefetch = transformedItems;
          }
        }

        // Fetch user profile to pre-fill shipping info
        const userResponse = await userAPI.getProfile();

        if (userResponse && userResponse.data) {
          const user = userResponse.data.user || userResponse.data;
          const profile = userResponse.data.profile || {};

          const extractedFirstName = user.first_name || user.firstName || profile.first_name || profile.firstName || '';
          const extractedLastName = user.last_name || user.lastName || profile.last_name || profile.lastName || '';
          const extractedEmail = user.email || profile.email || '';
          const extractedPhone = user.phone || profile.phone || '';


          setShippingInfo(prev => ({
            ...prev,
            firstName: extractedFirstName,
            lastName: extractedLastName,
            email: extractedEmail,
            phone: extractedPhone
          }));
        }

        // Fetch saved addresses and pre-fill with default address
        try {
          const addressesResponse = await userAPI.getAddresses();
          if (addressesResponse && addressesResponse.data) {
            const addresses = addressesResponse.data.addresses || addressesResponse.data || [];
            setSavedAddresses(Array.isArray(addresses) ? addresses : []);

            // Find default address and pre-fill the form
            const defaultAddress = addresses.find(addr => addr.isDefault || addr.is_default);
            // Whichever address we prefill below, quote real shipping for it
            // immediately (see fetchShippingRate call further down) instead
            // of only doing so once the buyer reaches and clicks past this
            // step — otherwise the Order Summary shown right now, on this
            // very step, is stuck on the flat placeholder for no reason when
            // we already know a real address to quote against.
            let resolvedAddressForPrefetch = null;
            if (defaultAddress) {
              setSelectedAddressId(defaultAddress.id || defaultAddress._id);
              setShippingInfo(prev => ({
                ...prev,
                // Preserve user's personal info (firstName, lastName, email, phone)
                // Only update address fields
                address: defaultAddress.street || '',
                city: defaultAddress.city || '',
                state: defaultAddress.state || '',
                zipCode: defaultAddress.postal_code || defaultAddress.postalCode || '',
                country: defaultAddress.country || 'Nigeria',
                landmark: defaultAddress.landmark || ''
              }));
              resolvedAddressForPrefetch = {
                address: defaultAddress.street || '', city: defaultAddress.city || '',
                state: defaultAddress.state || '', country: defaultAddress.country || 'Nigeria'
              };
            } else if (addresses.length > 0) {
              // If no default, use the first address
              const firstAddress = addresses[0];
              setSelectedAddressId(firstAddress.id || firstAddress._id);
              setShippingInfo(prev => ({
                ...prev,
                // Preserve user's personal info (firstName, lastName, email, phone)
                // Only update address fields
                address: firstAddress.street || '',
                city: firstAddress.city || '',
                state: firstAddress.state || '',
                zipCode: firstAddress.postal_code || firstAddress.postalCode || '',
                country: firstAddress.country || 'Nigeria',
                landmark: firstAddress.landmark || ''
              }));
              resolvedAddressForPrefetch = {
                address: firstAddress.street || '', city: firstAddress.city || '',
                state: firstAddress.state || '', country: firstAddress.country || 'Nigeria'
              };
            } else {
              // No saved addresses, show the form
              setShowAddressForm(true);
            }

            if (resolvedAddressForPrefetch) {
              fetchShippingRate(resolvedAddressForPrefetch, itemsForShippingPrefetch);
            }
          }
        } catch (addressError) {
          setShowAddressForm(true);
        }

        // Fetch wallet balance with enhanced parsing
        const walletResponse = await walletAPI.getBalance();

        if (walletResponse && walletResponse.data) {
          // Try multiple possible response structures
          let balanceData;

          if (walletResponse.data.data) {
            balanceData = walletResponse.data.data;
          } else if (walletResponse.data.balance !== undefined) {
            balanceData = walletResponse.data;
          } else {
            balanceData = walletResponse;
          }

          const newBalance = parseFloat(balanceData.balance) || 0;
          setWalletBalance(newBalance);
        }

        // Fetch saved payment methods
        try {
          const paymentMethodsResponse = await paymentMethodsAPI.getPaymentMethods();
          if (paymentMethodsResponse && paymentMethodsResponse.data) {
            const methods = Array.isArray(paymentMethodsResponse.data)
              ? paymentMethodsResponse.data
              : paymentMethodsResponse.data.payment_methods || [];
            setSavedPaymentMethods(methods);
          }
        } catch (paymentMethodError) {
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch checkout recommendations (Accessories) based on first cart item
  useEffect(() => {
    if (cartItems.length === 0) return;
    const fetchRecommendations = async () => {
      try {
        const firstProductId = cartItems[0]?.product_id || cartItems[0]?.product?._id || cartItems[0]?.product?.id;
        const res = await recommendationsAPI.getCheckoutRecommendations(firstProductId);
        const items = res?.data?.data || res?.data?.recommendations || res?.data || [];
        setCheckoutRecommendations(Array.isArray(items) ? items.slice(0, 4) : []);
      } catch (err) {
        setCheckoutRecommendations([]);
      }
    };
    fetchRecommendations();
  }, [cartItems]);

  // Calculate totals
  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => {
    return cartItems.length > 0 ? shippingFee : 0;
  };

  // Call shipping API with current address + cart — per doc Step 2.
  // Accepts explicit overrides so the mount-time prefetch (below, using a
  // saved default address) can pass the address/items it just resolved
  // directly — reading shippingInfo/cartItems state there would see stale,
  // pre-update values from the same async effect's closure.
  const fetchShippingRate = async (addressOverride, itemsOverride) => {
    const addr = addressOverride || shippingInfo;
    const sourceItems = itemsOverride || cartItems;
    if (!addr.address || !addr.city || !addr.state || sourceItems.length === 0) return;
    const deliveryAddress = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country || 'Nigeria'}`;
    const items = sourceItems.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
    setCalculatingShipping(true);
    try {
      const response = await shippingAPI.calculateShipping({ address: deliveryAddress, items });
      const data = response?.data?.data || response?.data;
      if (data?.shipping_fee != null) {
        setShippingFee(data.shipping_fee);
        setShippingEstimated(data.estimated === true);
      }
    } catch {
      // Keep ₦5,000 fallback — doc says system returns estimated=true on FabsTech failure
      setShippingFee(5000);
      setShippingEstimated(true);
    } finally {
      setCalculatingShipping(false);
    }
  };

  // Logistics VAT — VAT on the shipping fee only, never on the product
  // price. No general buyer-facing product tax exists; VAT is otherwise
  // only charged to the seller, on their commission.
  const calculateLogisticsVatAmount = () => calculateLogisticsVat(calculateShipping(), vatPercentage);

  // The combined figure shown to the buyer: shipping fee + VAT on it.
  const calculateLogisticsCharge = () => calculateShipping() + calculateLogisticsVatAmount();

  // Escrow Fee — a percentage of the product subtotal, only charged when
  // the buyer opts into escrow protection.
  const calculateEscrowFeeAmount = () => useEscrow ? calculateEscrowFee(calculateSubtotal(), escrowFeePercentage) : 0;

  // Backend-computed at apply time (Cart.jsx) and re-validated on this page
  // (see the appliedPromo effect below) — never re-derived from promo type
  // client-side, so it can't drift from what CreateOrder will actually charge.
  const calculateDiscount = () => appliedPromo?.discount_amount || 0;

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const logisticsCharge = calculateLogisticsCharge();
    const escrowFee = calculateEscrowFeeAmount();
    const discount = calculateDiscount();

    return Math.max(0, subtotal + logisticsCharge + escrowFee - discount);
  };

  // Re-validate any promo code applied back on the Cart page — the subtotal
  // here may differ (items removed, quantities changed), and the code could
  // have expired or hit its usage limit in the interim.
  useEffect(() => {
    if (cartItems.length === 0) return;
    const stored = sessionStorage.getItem(APPLIED_PROMO_STORAGE_KEY);
    if (!stored) return;
    let code;
    try { code = JSON.parse(stored)?.code; } catch { code = null; }
    if (!code) return;

    promotionsAPI.validate(code, calculateSubtotal())
      .then(res => {
        const data = res?.data || res;
        setAppliedPromo({ code: data.code || code, type: data.type, discount_amount: data.discount_amount || 0, description: data.description || '' });
      })
      .catch(() => {
        sessionStorage.removeItem(APPLIED_PROMO_STORAGE_KEY);
        setAppliedPromo(null);
      });
    // Only re-check when the cart contents actually change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length]);

  // Validate shipping form
  const validateShipping = () => {
    const newErrors = {};

    if (!shippingInfo.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!shippingInfo.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!shippingInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(shippingInfo.email)) newErrors.email = 'Email is invalid';
    if (!shippingInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!shippingInfo.address.trim()) newErrors.address = 'Address is required';
    if (!shippingInfo.city.trim()) newErrors.city = 'City is required';
    if (!shippingInfo.state.trim()) newErrors.state = 'State is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle next step
  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!validateShipping()) {
        return;
      }
      // Fetch real shipping rate from FabsTech API (doc Step 2) and wait for
      // it — advancing before this resolves let the buyer see a stale
      // fallback total that then silently changed underneath them.
      await fetchShippingRate();
    }

    if (currentStep === 2) {
      // Validate payment method
      if (paymentMethod === 'wallet' && walletBalance < calculateTotal()) {
        showCustomToast({ type: 'warning', message: 'Insufficient wallet balance. Please top up your wallet or choose another payment method.' });
        return;
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  // Group cart items by seller and build per-seller order data
  const buildSellerOrders = () => {
    const sellerGroups = {};
    cartItems.forEach(item => {
      const sellerId = item.seller_id || 'unknown';
      if (!sellerGroups[sellerId]) sellerGroups[sellerId] = [];
      sellerGroups[sellerId].push(item);
    });

    const totalSubtotal = calculateSubtotal();
    const totalShipping = calculateShipping();
    // The backend recomputes Logistics VAT and Escrow Fee authoritatively
    // from each order's own subtotal/shipping (see CreateOrder) — these
    // per-group figures are for display/preview parity only, using the same
    // formulas so what the buyer sees here matches what gets charged. The
    // promo code is likewise only a hint (promo_code) — CreateOrder looks it
    // up and computes each group's own discount from its own subtotal,
    // exactly like escrow fee/VAT above.
    const totalDiscount = calculateDiscount();

    return Object.values(sellerGroups).map(items => {
      const groupSubtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const proportion = totalSubtotal > 0 ? groupSubtotal / totalSubtotal : 1;
      const groupShipping = Math.round(totalShipping * proportion);
      const groupLogisticsVat = calculateLogisticsVat(groupShipping, vatPercentage);
      const groupEscrowFee = useEscrow ? calculateEscrowFee(groupSubtotal, escrowFeePercentage) : 0;
      const groupDiscount = Math.round(totalDiscount * proportion);
      const groupTotal = Math.max(0, groupSubtotal + groupShipping + groupLogisticsVat + groupEscrowFee - groupDiscount);

      return {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          seller_id: item.seller_id,
          name: item.name
        })),
        shipping_address: {
          first_name: shippingInfo.firstName,
          last_name: shippingInfo.lastName,
          email: shippingInfo.email,
          phone: shippingInfo.phone,
          street: shippingInfo.address,
          city: shippingInfo.city,
          state: shippingInfo.state,
          zip_code: shippingInfo.zipCode,
          country: shippingInfo.country,
          landmark: shippingInfo.landmark || ''
        },
        payment_method: paymentMethod === 'saved' ? 'card' : paymentMethod,
        use_escrow: useEscrow,
        subtotal: groupSubtotal,
        tax_amount: groupLogisticsVat,
        escrow_fee_amount: groupEscrowFee,
        shipping_cost: groupShipping,
        promo_code: appliedPromo?.code || undefined,
        total: groupTotal
      };
    });
  };

  // Handle place order — creates one order per seller to comply with backend constraint
  const handlePlaceOrder = async () => {
    try {
      setProcessing(true);

      // Save address if checkbox is checked and it's a new address
      if (shippingInfo.saveAddress && (showAddressForm || savedAddresses.length === 0)) {
        try {
          const addressData = {
            type: 'home',
            street: shippingInfo.address,
            city: shippingInfo.city,
            state: shippingInfo.state,
            country: shippingInfo.country,
            postal_code: shippingInfo.zipCode,
            landmark: shippingInfo.landmark || '',
            isDefault: savedAddresses.length === 0
          };
          await userAPI.createAddress(addressData);
        } catch (addressError) {
          // Continue with order even if address save fails
        }
      }

      // Split cart into per-seller order payloads
      const sellerOrderPayloads = buildSellerOrders();

      if (paymentMethod === 'card' || paymentMethod === 'saved' || paymentMethod === 'transfer') {
        // Create one order per seller, then initialize a single Monnify payment for the full total
        const createdOrders = [];
        for (const orderData of sellerOrderPayloads) {
          const orderResponse = await ordersAPI.create(orderData);
          if (!orderResponse || !orderResponse.data) {
            throw new Error('Failed to create order');
          }
          createdOrders.push(orderResponse.data.order || orderResponse.data);
        }

        const primaryOrder = createdOrders[0];
        const primaryOrderId = primaryOrder._id || primaryOrder.id;
        // Sum the actually-created orders' server-computed totals rather than
        // recomputing client-side here — this call can run moments after
        // buildSellerOrders() snapshotted shippingFee, and re-deriving the
        // total from live component state a second time let it drift from
        // what was actually persisted (and is what CreateOrder/InitializePayment
        // will charge regardless of what's sent here).
        const finalTotal = createdOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0) || calculateTotal();

        const paymentData = {
          amount: parseInt(finalTotal),
          payment_type: 'order',
          currency: 'NGN',
          callback_url: `${window.location.origin}/order-confirmation/${primaryOrderId}?payment=success`,
          order_id: primaryOrderId,
          // A multi-seller cart creates one order per seller — order_ids
          // (read server-side, not just carried in metadata) lets the backend
          // charge the combined total once and mark every one of them paid,
          // instead of only ever confirming primaryOrder while the rest sit
          // permanently unpaid despite their inventory already being deducted.
          order_ids: createdOrders.map(o => o._id || o.id),
          save_payment_method: savePaymentMethod,
          payment_method_id: paymentMethod === 'saved' ? selectedSavedMethod : undefined,
          metadata: {
            order_id: primaryOrderId,
            order_ids: createdOrders.map(o => o._id || o.id),
            order_number: primaryOrder.order_number || primaryOrder.orderNumber,
            payment_method: paymentMethod,
            use_escrow: useEscrow,
            subtotal: calculateSubtotal(),
            shipping: calculateShipping(),
            logistics_vat: calculateLogisticsVatAmount(),
            escrow_fee: calculateEscrowFeeAmount(),
            discount: calculateDiscount(),
            promo_code: appliedPromo?.code,
            total: finalTotal
          }
        };

        // The orders are already created with their discount applied server-side
        // (CreateOrder resolved promo_code above); clear the stored code now so
        // it doesn't silently carry over into an unrelated future checkout.
        sessionStorage.removeItem(APPLIED_PROMO_STORAGE_KEY);

        const paymentResponse = await paymentAPI.initializePayment(paymentData);

        if (paymentResponse && paymentResponse.data && paymentResponse.data.checkout_url) {
          window.location.href = paymentResponse.data.checkout_url;
        } else {
          throw new Error('Failed to initialize payment');
        }
      } else if (paymentMethod === 'wallet') {
        // Create one order per seller, wallet is debited per order on backend
        const createdOrders = [];
        for (const orderData of sellerOrderPayloads) {
          const orderResponse = await ordersAPI.create(orderData);
          if (!orderResponse || !orderResponse.data) {
            throw new Error('Failed to create order');
          }
          createdOrders.push(orderResponse.data.order || orderResponse.data);
        }

        // The orders are already created with their discount applied
        // server-side; clear the stored code so it doesn't carry into a
        // future, unrelated checkout.
        sessionStorage.removeItem(APPLIED_PROMO_STORAGE_KEY);

        // Clear cart after all orders succeed (only if not Buy Now)
        if (!isBuyNow) {
          await clearCart();
        }

        // Fetch updated wallet balance to reflect deduction
        try {
          const updatedWalletResponse = await walletAPI.getBalance();

          if (updatedWalletResponse && updatedWalletResponse.data) {
            let balanceData;

            if (updatedWalletResponse.data.data) {
              balanceData = updatedWalletResponse.data.data;
            } else if (updatedWalletResponse.data.balance !== undefined) {
              balanceData = updatedWalletResponse.data;
            } else {
              balanceData = updatedWalletResponse;
            }

            const newBalance = parseFloat(balanceData.balance) || 0;
            setWalletBalance(newBalance);

            showCustomToast({ type: 'success', message: `Order${createdOrders.length > 1 ? 's' : ''} placed successfully using wallet! New balance: ₦${newBalance.toLocaleString()}` });
          } else {
            showCustomToast({ type: 'success', message: 'Order placed successfully using wallet!' });
          }
        } catch (walletError) {
          showCustomToast({ type: 'success', message: 'Order placed successfully using wallet!' });
        }

        const primaryOrder = createdOrders[0];
        navigate(`/order-confirmation/${primaryOrder._id || primaryOrder.id}`);
      }
    } catch (error) {
      if (!error.isAuthError) showCustomToast({ type: 'warning', message: error.message || 'Failed to place order. Please try again.' });
    } finally {
      // Previously only reset for wallet — for card/saved/transfer, any
      // thrown error (Pandascrow down, "Escrow payment initialization
      // failed", network error) left the Pay button stuck on "Processing..."
      // permanently, since nothing ever cleared it. Safe to always reset:
      // the success path redirects away via window.location.href right
      // after this runs, so resetting state on an about-to-unload page is a
      // no-op there.
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => `₦${amount.toLocaleString()}`;

  // Loading state
  if (loading) {
    return (
      <div className="tw-min-h-[70vh] tw-pt-[100px] sm:tw-pt-[110px] md:tw-pt-[140px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-bg-zinc-50 dark:tw-bg-zinc-950">
        <FontAwesomeIcon icon={faSpinner} spin className="tw-text-4xl tw-text-green-500" />
        <p className="tw-text-zinc-500 dark:tw-text-zinc-400 tw-font-medium">Loading checkout...</p>
      </div>
    );
  }

  // Empty cart
  if (cartItems.length === 0) {
    return (
      <div className="tw-min-h-[70vh] tw-pt-[100px] sm:tw-pt-[110px] md:tw-pt-[140px] tw-flex tw-items-center tw-justify-center tw-bg-zinc-50 dark:tw-bg-zinc-950 tw-px-4">
        <div className="tw-text-center tw-max-w-sm">
          <FontAwesomeIcon icon={faShoppingCart} className="tw-text-5xl tw-text-zinc-300 dark:tw-text-zinc-700 tw-mb-5" />
          <h2 className="tw-text-xl tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50 tw-mb-2">Your cart is empty</h2>
          <p className="tw-text-zinc-500 dark:tw-text-zinc-400 tw-mb-6">Add items to your cart before checking out</p>
          <button
            onClick={() => navigate('/shop')}
            className={BTN_PRIMARY}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const steps = [
    { n: 1, label: 'Shipping' },
    { n: 2, label: 'Payment' },
    { n: 3, label: 'Review' },
  ];

  const showEscrowOption = paymentMethod === 'card' || paymentMethod === 'saved' || paymentMethod === 'transfer';

  return (
    <div className="tw-min-h-screen tw-bg-zinc-50 dark:tw-bg-zinc-950 tw-pt-[100px] sm:tw-pt-[110px] md:tw-pt-[140px] tw-pb-10 lg:tw-pb-14">
      <div className="tw-max-w-[1600px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-10">

        {/* Progress Steps */}
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 sm:tw-gap-6 tw-mb-10">
          {steps.map((step, i) => {
            const active = currentStep >= step.n;
            const completed = currentStep > step.n;
            return (
              <div key={step.n} className="tw-flex tw-items-center tw-gap-3 sm:tw-gap-6">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                  <div
                    className={`tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-transition-all tw-duration-300 ${
                      completed || active
                        ? 'tw-bg-[linear-gradient(135deg,#22C55E_0%,#16A34A_100%)] tw-text-white tw-shadow-[0_4px_16px_-2px_rgba(34,197,94,0.5)]'
                        : 'tw-bg-white dark:tw-bg-zinc-800 tw-text-zinc-400 dark:tw-text-zinc-500 tw-border tw-border-zinc-200 dark:tw-border-zinc-700'
                    }`}
                  >
                    {completed ? <FontAwesomeIcon icon={faCheckCircle} /> : step.n}
                  </div>
                  <span className={`tw-hidden sm:tw-inline tw-text-sm tw-font-semibold ${active ? 'tw-text-zinc-900 dark:tw-text-zinc-50' : 'tw-text-zinc-400 dark:tw-text-zinc-500'}`}>
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`tw-w-8 sm:tw-w-16 tw-h-0.5 tw-rounded-full ${currentStep > step.n ? 'tw-bg-green-500' : 'tw-bg-zinc-200 dark:tw-bg-zinc-700'}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1fr_440px] tw-gap-6 xl:tw-gap-10 tw-items-start">

          {/* Left Column - Forms */}
          <div className="tw-flex tw-flex-col tw-gap-6">

            {/* Step 1: Shipping Information */}
            {currentStep === 1 && (
              <SectionCard icon={faTruck} title="Shipping Information">

                {/* Saved Addresses Section */}
                {savedAddresses.length > 0 && (
                  <div className="tw-mb-7">
                    <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="tw-text-green-500" />
                      Select Delivery Address
                    </h3>
                    <div className="tw-grid sm:tw-grid-cols-2 tw-gap-3">
                      {savedAddresses.map((addr) => {
                        const addrId = addr.id || addr._id;
                        const isSelected = selectedAddressId === addrId;
                        const isDefault = addr.isDefault || addr.is_default;
                        const typeIcon = addr.type === 'home' ? faHome : addr.type === 'work' ? faBriefcase : faEllipsisH;

                        return (
                          <div
                            key={addrId}
                            onClick={() => {
                              setSelectedAddressId(addrId);
                              setShippingInfo(prev => ({
                                ...prev,
                                address: addr.street || '',
                                city: addr.city || '',
                                state: addr.state || '',
                                zipCode: addr.postal_code || addr.postalCode || '',
                                country: addr.country || 'Nigeria',
                                landmark: addr.landmark || ''
                              }));
                              setShowAddressForm(false);
                            }}
                            className={`tw-p-4 tw-rounded-2xl tw-cursor-pointer tw-transition-all tw-duration-200 tw-border-2 ${
                              isSelected
                                ? 'tw-border-green-500 tw-bg-green-500/5 tw-shadow-[0_4px_20px_-6px_rgba(34,197,94,0.3)]'
                                : 'tw-border-zinc-200 dark:tw-border-zinc-700 hover:tw-border-green-500/40'
                            }`}
                          >
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                              <div className="tw-flex tw-items-center tw-gap-2">
                                <FontAwesomeIcon icon={typeIcon} className="tw-text-green-500 tw-text-sm" />
                                <span className="tw-font-semibold tw-capitalize tw-text-sm tw-text-zinc-800 dark:tw-text-zinc-100">{addr.type || 'Address'}</span>
                                {isDefault && (
                                  <span className="tw-text-[0.65rem] tw-font-bold tw-bg-green-500/10 tw-text-green-600 tw-px-2 tw-py-0.5 tw-rounded-full tw-flex tw-items-center tw-gap-1">
                                    <FontAwesomeIcon icon={faStar} className="tw-text-[0.55rem]" /> Default
                                  </span>
                                )}
                              </div>
                              {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="tw-text-green-500" />}
                            </div>
                            <p className="tw-text-[0.875rem] tw-text-zinc-700 dark:tw-text-zinc-300 tw-mb-0.5">{addr.street}</p>
                            <p className="tw-text-[0.8rem] tw-text-zinc-500 dark:tw-text-zinc-400">
                              {addr.city}, {addr.state} {addr.postal_code || addr.postalCode}
                            </p>
                            {addr.landmark && (
                              <p className="tw-text-[0.75rem] tw-text-zinc-400 dark:tw-text-zinc-500 tw-italic tw-mt-1">Landmark: {addr.landmark}</p>
                            )}
                          </div>
                        );
                      })}

                      {/* Add New Address Button */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAddressId(null);
                          setShippingInfo(prev => ({
                            ...prev,
                            address: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            country: 'Nigeria',
                            landmark: ''
                          }));
                          setShowAddressForm(true);
                        }}
                        className="tw-p-4 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-zinc-300 dark:tw-border-zinc-600 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-zinc-500 dark:tw-text-zinc-400 tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 hover:tw-border-green-500 hover:tw-text-green-500 hover:tw-bg-green-500/5"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add New Address
                      </button>
                    </div>
                  </div>
                )}

                {/* Address Form heading - Show when adding new or when no saved addresses */}
                {(showAddressForm || savedAddresses.length === 0) && savedAddresses.length > 0 && (
                  <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-4">New Address Details</h3>
                )}

                <div className="tw-grid sm:tw-grid-cols-2 tw-gap-4">
                  <Field label="First Name *" icon={faUser} error={errors.firstName}>
                    <TextInput type="text" name="firstName" value={shippingInfo.firstName} onChange={handleInputChange} error={errors.firstName} placeholder="Enter first name" />
                  </Field>

                  <Field label="Last Name *" icon={faUser} error={errors.lastName}>
                    <TextInput type="text" name="lastName" value={shippingInfo.lastName} onChange={handleInputChange} error={errors.lastName} placeholder="Enter last name" />
                  </Field>

                  <Field label="Email *" icon={faEnvelope} error={errors.email}>
                    <TextInput type="email" name="email" value={shippingInfo.email} onChange={handleInputChange} error={errors.email} placeholder="your@email.com" />
                  </Field>

                  <Field label="Phone Number *" icon={faPhone} error={errors.phone}>
                    <TextInput type="tel" name="phone" value={shippingInfo.phone} onChange={handleInputChange} error={errors.phone} placeholder="+234 800 000 0000" />
                  </Field>

                  {/* Address Fields - Show when adding new or when no saved addresses exist */}
                  {(showAddressForm || savedAddresses.length === 0) && (
                    <>
                      <div className="sm:tw-col-span-2">
                        <Field label="Address *" icon={faMapMarkerAlt} error={errors.address}>
                          <TextInput type="text" name="address" value={shippingInfo.address} onChange={handleInputChange} error={errors.address} placeholder="Street address, P.O. box" />
                        </Field>
                      </div>

                      <Field label="City *" error={errors.city}>
                        <TextInput type="text" name="city" value={shippingInfo.city} onChange={handleInputChange} error={errors.city} placeholder="Enter city" />
                      </Field>

                      <Field label="State *" error={errors.state}>
                        <TextInput type="text" name="state" value={shippingInfo.state} onChange={handleInputChange} error={errors.state} placeholder="Enter state" />
                      </Field>

                      <Field label="Zip Code">
                        <TextInput type="text" name="zipCode" value={shippingInfo.zipCode} onChange={handleInputChange} placeholder="100001" />
                      </Field>

                      <Field label="Country">
                        <TextInput type="text" name="country" value={shippingInfo.country} onChange={handleInputChange} disabled className="tw-opacity-60 tw-cursor-not-allowed" />
                      </Field>

                      <div className="sm:tw-col-span-2">
                        <Field label="Landmark (Optional)">
                          <TextInput type="text" name="landmark" value={shippingInfo.landmark} onChange={handleInputChange} placeholder="Nearby landmark for easier delivery" />
                        </Field>
                      </div>
                    </>
                  )}
                </div>

                {/* Save Address Checkbox - Only show when adding new address */}
                {(showAddressForm || savedAddresses.length === 0) && (
                  <label className="tw-flex tw-items-center tw-gap-2.5 tw-mt-5 tw-text-sm tw-text-zinc-600 dark:tw-text-zinc-300 tw-cursor-pointer">
                    <input
                      type="checkbox"
                      name="saveAddress"
                      checked={shippingInfo.saveAddress}
                      onChange={handleInputChange}
                      className="tw-w-4 tw-h-4 tw-accent-green-500"
                    />
                    Save this address for future orders
                  </label>
                )}
              </SectionCard>
            )}

            {/* Step 2: Payment Method */}
            {currentStep === 2 && (
              <SectionCard icon={faCreditCard} title="Payment Method">
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {[
                    { id: 'card', icon: faCreditCard, title: 'Debit/Credit Card', sub: 'Pay securely with your card via Monnify' },
                    { id: 'wallet', icon: faWallet, title: 'Wallet Payment', sub: `Balance: ${formatCurrency(walletBalance)}`, warn: walletBalance < calculateTotal() ? 'Insufficient balance' : null },
                    { id: 'transfer', icon: faBuilding, title: 'Bank Transfer', sub: 'Pay via direct bank transfer' },
                  ].map(opt => (
                    <label
                      key={opt.id}
                      onClick={() => setPaymentMethod(opt.id)}
                      className={`tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-2xl tw-border-2 tw-cursor-pointer tw-transition-all tw-duration-200 ${
                        paymentMethod === opt.id
                          ? 'tw-border-green-500 tw-bg-green-500/5 tw-shadow-[0_4px_20px_-6px_rgba(34,197,94,0.3)]'
                          : 'tw-border-zinc-200 dark:tw-border-zinc-700 hover:tw-border-green-500/40'
                      }`}
                    >
                      <input type="radio" name="paymentMethod" value={opt.id} checked={paymentMethod === opt.id} onChange={(e) => setPaymentMethod(e.target.value)} className="tw-sr-only" />
                      <RadioDot checked={paymentMethod === opt.id} />
                      <span className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-green-500/10 tw-text-green-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <FontAwesomeIcon icon={opt.icon} />
                      </span>
                      <span className="tw-flex-1">
                        <span className="tw-block tw-font-semibold tw-text-sm tw-text-zinc-900 dark:tw-text-zinc-50">{opt.title}</span>
                        <span className="tw-block tw-text-[0.8rem] tw-text-zinc-500 dark:tw-text-zinc-400">{opt.sub}</span>
                        {opt.warn && <span className="tw-block tw-text-[0.75rem] tw-font-semibold tw-text-red-500 tw-mt-0.5">{opt.warn}</span>}
                      </span>
                    </label>
                  ))}

                  {/* Saved Payment Methods */}
                  {savedPaymentMethods.length > 0 && (
                    <div className="tw-mt-2">
                      <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-3">Saved Payment Methods</h3>
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        {savedPaymentMethods.map((method) => {
                          const methodId = method._id || method.id;
                          const selected = paymentMethod === 'saved' && selectedSavedMethod === methodId;
                          return (
                            <label
                              key={methodId}
                              onClick={() => { setPaymentMethod('saved'); setSelectedSavedMethod(methodId); }}
                              className={`tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-2xl tw-border-2 tw-cursor-pointer tw-transition-all tw-duration-200 ${
                                selected ? 'tw-border-green-500 tw-bg-green-500/5 tw-shadow-[0_4px_20px_-6px_rgba(34,197,94,0.3)]' : 'tw-border-zinc-200 dark:tw-border-zinc-700 hover:tw-border-green-500/40'
                              }`}
                            >
                              <input type="radio" name="paymentMethod" value="saved" checked={selected} onChange={() => { setPaymentMethod('saved'); setSelectedSavedMethod(methodId); }} className="tw-sr-only" />
                              <RadioDot checked={selected} />
                              <span className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-green-500/10 tw-text-green-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <FontAwesomeIcon icon={faCreditCard} />
                              </span>
                              <span className="tw-flex-1">
                                <span className="tw-block tw-font-semibold tw-text-sm tw-text-zinc-900 dark:tw-text-zinc-50">
                                  {method.card_type || method.type || 'Card'} •••• {method.last4 || method.last_four || '****'}
                                </span>
                                <span className="tw-block tw-text-[0.8rem] tw-text-zinc-500 dark:tw-text-zinc-400">
                                  {method.card_brand || method.brand || 'Card'}
                                  {method.expiry_month && method.expiry_year && ` • Expires ${method.expiry_month}/${method.expiry_year}`}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Save Payment Method Checkbox - Only show for card payment */}
                {paymentMethod === 'card' && (
                  <label className="tw-flex tw-items-start tw-gap-3 tw-mt-5 tw-p-4 tw-rounded-xl tw-bg-zinc-50 dark:tw-bg-zinc-800/50 tw-cursor-pointer">
                    <input type="checkbox" checked={savePaymentMethod} onChange={(e) => setSavePaymentMethod(e.target.checked)} className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-green-500" />
                    <span>
                      <FontAwesomeIcon icon={faCreditCard} className="tw-text-green-500 tw-mr-2" />
                      <strong className="tw-text-sm tw-text-zinc-900 dark:tw-text-zinc-50">Save this payment method</strong>
                      <span className="tw-block tw-text-[0.8rem] tw-text-zinc-500 dark:tw-text-zinc-400 tw-mt-0.5">Securely save your card for faster checkout next time</span>
                    </span>
                  </label>
                )}

                {showEscrowOption && (
                  <label className="tw-flex tw-items-start tw-gap-3 tw-mt-3 tw-p-4 tw-rounded-xl tw-bg-green-500/5 tw-border tw-border-green-500/15 tw-cursor-pointer">
                    <input type="checkbox" checked={useEscrow} onChange={(e) => setUseEscrow(e.target.checked)} className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-green-500" />
                    <span>
                      <FontAwesomeIcon icon={faLock} className="tw-text-green-500 tw-mr-2" />
                      <strong className="tw-text-sm tw-text-zinc-900 dark:tw-text-zinc-50">Use Escrow Protection</strong>
                      <span className="tw-block tw-text-[0.8rem] tw-text-zinc-500 dark:tw-text-zinc-400 tw-mt-0.5">Your payment is held securely by Pandascrow until you confirm delivery</span>
                    </span>
                  </label>
                )}
              </SectionCard>
            )}

            {/* Step 3: Review Order */}
            {currentStep === 3 && (
              <SectionCard icon={faCheckCircle} title="Review Your Order">
                <div className="tw-flex tw-flex-col tw-gap-6">

                  <div>
                    <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-2">Shipping Information</h3>
                    <div className="tw-text-[0.875rem] tw-text-zinc-600 dark:tw-text-zinc-300 tw-leading-relaxed">
                      <p className="tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50">{shippingInfo.firstName} {shippingInfo.lastName}</p>
                      <p>{shippingInfo.email}</p>
                      <p>{shippingInfo.phone}</p>
                      <p>{shippingInfo.address}</p>
                      <p>{shippingInfo.city}, {shippingInfo.state} {shippingInfo.zipCode}</p>
                      <p>{shippingInfo.country}</p>
                    </div>
                  </div>

                  <div className="tw-border-t tw-border-zinc-100 dark:tw-border-zinc-800 tw-pt-6">
                    <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-2">Payment Method</h3>
                    <p className="tw-text-[0.875rem] tw-text-zinc-600 dark:tw-text-zinc-300">
                      <FontAwesomeIcon icon={
                        paymentMethod === 'card' ? faCreditCard :
                        paymentMethod === 'wallet' ? faWallet :
                        paymentMethod === 'saved' ? faCreditCard : faBuilding
                      } className="tw-mr-2 tw-text-green-500" />
                      {paymentMethod === 'card' ? 'Debit/Credit Card' :
                       paymentMethod === 'wallet' ? 'Wallet Payment' :
                       paymentMethod === 'saved' ? (() => {
                         const method = savedPaymentMethods.find(m => (m._id || m.id) === selectedSavedMethod);
                         return method ? `${method.card_type || method.type || 'Card'} •••• ${method.last4 || method.last_four || '****'}` : 'Saved Payment Method';
                       })() : 'Bank Transfer'}
                    </p>
                    {useEscrow && showEscrowOption && (
                      <p className="tw-inline-flex tw-items-center tw-gap-1.5 tw-mt-2 tw-text-xs tw-font-bold tw-text-green-600 tw-bg-green-500/10 tw-px-2.5 tw-py-1 tw-rounded-full">
                        <FontAwesomeIcon icon={faLock} /> Escrow Protected
                      </p>
                    )}
                  </div>

                  <div className="tw-border-t tw-border-zinc-100 dark:tw-border-zinc-800 tw-pt-6">
                    <h3 className="tw-text-sm tw-font-bold tw-text-zinc-700 dark:tw-text-zinc-200 tw-mb-3">Order Items</h3>
                    <div className="tw-flex tw-flex-col tw-gap-3">
                      {cartItems.map(item => (
                        <div key={item.id} className="tw-flex tw-items-center tw-gap-4">
                          <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name} className="tw-w-14 tw-h-14 tw-rounded-lg tw-object-cover tw-border tw-border-zinc-200 dark:tw-border-zinc-700 tw-flex-shrink-0" />
                          <div className="tw-flex-1 tw-min-w-0">
                            <h4 className="tw-text-sm tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50 tw-truncate">{item.name}</h4>
                            <p className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400">Quantity: {item.quantity}</p>
                          </div>
                          <div className="tw-font-bold tw-text-sm tw-text-zinc-900 dark:tw-text-zinc-50 tw-whitespace-nowrap">
                            {formatCurrency(item.price * item.quantity)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Navigation Buttons */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              {currentStep > 1 ? (
                <button
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={processing}
                  className={BTN_SECONDARY}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
              ) : <span />}

              {currentStep < 3 ? (
                <button
                  onClick={handleNextStep}
                  disabled={calculatingShipping}
                  className={BTN_PRIMARY}
                >
                  {calculatingShipping ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Calculating shipping...</>
                  ) : (
                    <>Continue <FontAwesomeIcon icon={faArrowRight} className="tw-text-xs" /></>
                  )}
                </button>
              ) : (
                <button
                  onClick={handlePlaceOrder}
                  disabled={processing}
                  className={BTN_PRIMARY}
                >
                  {processing ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                  ) : (
                    <><FontAwesomeIcon icon={faCheckCircle} /> Place Order</>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="tw-flex tw-flex-col tw-gap-6 lg:tw-sticky lg:tw-top-8">
            <div className={`${CARD} tw-p-7 md:tw-p-8`}>
              <h3 className="tw-text-lg tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50 tw-pb-5 tw-mb-5 tw-border-b tw-border-zinc-100 dark:tw-border-zinc-800">
                Order Summary
              </h3>

              <div className="tw-flex tw-flex-col tw-gap-4 tw-mb-5">
                {cartItems.map(item => (
                  <div key={item.id} className="tw-flex tw-items-center tw-gap-4">
                    <img src={item.image || 'https://via.placeholder.com/50'} alt={item.name} className="tw-w-16 tw-h-16 tw-rounded-xl tw-object-cover tw-border tw-border-zinc-200 dark:tw-border-zinc-700 tw-flex-shrink-0" />
                    <div className="tw-flex-1 tw-min-w-0">
                      <p className="tw-text-sm tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50 tw-truncate">{item.name}</p>
                      <span className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400">Qty: {item.quantity}</span>
                    </div>
                    <span className="tw-font-bold tw-text-sm tw-text-green-600 dark:tw-text-green-400 tw-whitespace-nowrap">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="tw-border-t tw-border-zinc-100 dark:tw-border-zinc-800 tw-my-5" />

              <div className="tw-flex tw-flex-col tw-gap-3 tw-text-[0.9rem]">
                <div className="tw-flex tw-justify-between tw-items-center tw-text-zinc-600 dark:tw-text-zinc-300">
                  <span>Subtotal</span>
                  <span className="tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50">{formatCurrency(calculateSubtotal())}</span>
                </div>

                <div className="tw-flex tw-justify-between tw-items-center tw-text-zinc-600 dark:tw-text-zinc-300">
                  <span>
                    Logistics Charge
                    {calculateShipping() > 0 && <span className="tw-text-[0.7rem] tw-ml-1.5 tw-opacity-60">(VAT included)</span>}
                    {shippingEstimated && <span className="tw-text-[0.7rem] tw-ml-1.5 tw-font-bold tw-text-amber-500">est.</span>}
                  </span>
                  <span className="tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50">
                    {calculatingShipping
                      ? <FontAwesomeIcon icon={faSpinner} spin className="tw-text-zinc-400" />
                      : formatCurrency(calculateLogisticsCharge())}
                  </span>
                </div>

                {useEscrow && (
                  <div className="tw-flex tw-justify-between tw-items-center tw-text-zinc-600 dark:tw-text-zinc-300">
                    <span>Escrow Fee ({escrowFeePercentage}%)</span>
                    <span className="tw-font-semibold tw-text-zinc-900 dark:tw-text-zinc-50">{formatCurrency(calculateEscrowFeeAmount())}</span>
                  </div>
                )}

                {appliedPromo && (
                  <div className="tw-flex tw-justify-between tw-items-center tw-text-green-600 dark:tw-text-green-400">
                    <span>Discount ({appliedPromo.code})</span>
                    <span className="tw-font-semibold">-{formatCurrency(calculateDiscount())}</span>
                  </div>
                )}
              </div>

              <div className="tw-flex tw-justify-between tw-items-center tw-mt-5 -tw-mx-7 md:-tw-mx-8 tw-px-7 md:tw-px-8 tw-py-5 tw-bg-[linear-gradient(135deg,rgba(34,197,94,0.08)_0%,rgba(22,163,74,0.04)_100%)] tw-border-t tw-border-green-600/10 dark:tw-border-green-500/15">
                <span className="tw-text-base tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50">Total</span>
                <span className="tw-text-[1.75rem] tw-font-extrabold tw-tracking-tight tw-text-green-600 dark:tw-text-green-400">{formatCurrency(calculateTotal())}</span>
              </div>

              {useEscrow && showEscrowOption && (
                <div className="tw-flex tw-items-start tw-gap-2.5 tw-mt-5 tw-p-4 tw-rounded-xl tw-bg-green-500/5 tw-border tw-border-green-500/15">
                  <FontAwesomeIcon icon={faLock} className="tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" />
                  <p className="tw-text-[0.8rem] tw-text-zinc-600 dark:tw-text-zinc-300 tw-leading-relaxed tw-m-0">
                    Your payment will be held in escrow by Pandascrow until delivery is confirmed
                  </p>
                </div>
              )}
            </div>

            {/* Checkout Recommendations (Accessories) */}
            {checkoutRecommendations.length > 0 && (
              <div className={`${CARD} tw-p-6`}>
                <h4 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-zinc-900 dark:tw-text-zinc-50 tw-mb-4">
                  <FontAwesomeIcon icon={faStar} className="tw-text-green-500" />
                  You might also like
                </h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  {checkoutRecommendations.map(item => {
                    const itemId = item._id || item.id;
                    const itemImage = item.images?.[0]?.url || item.images?.[0] || item.image || 'https://via.placeholder.com/80';
                    const itemName = item.title || item.name || 'Accessory';
                    const itemPrice = item.price || 0;
                    return (
                      <div
                        key={itemId}
                        onClick={() => navigate(`/product/${itemId}`)}
                        className="tw-cursor-pointer tw-group"
                      >
                        <img src={itemImage} alt={itemName} className="tw-w-full tw-aspect-square tw-rounded-xl tw-object-cover tw-border tw-border-zinc-200 dark:tw-border-zinc-700 tw-mb-2 tw-transition-transform group-hover:tw-scale-[1.02]" />
                        <p className="tw-text-xs tw-font-medium tw-text-zinc-700 dark:tw-text-zinc-300 tw-truncate">
                          {itemName.length > 30 ? itemName.slice(0, 30) + '...' : itemName}
                        </p>
                        <span className="tw-text-xs tw-font-bold tw-text-green-600 dark:tw-text-green-400">
                          {formatCurrency(itemPrice)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
